import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

// MUI
import Icon from '@mui/material/Icon';

// MUI Icons
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

// Custom Components
import { SidebarContent, SidebarControl, SidebarHeading, SidebarPane, SidebarTabList, SidebarTabListItem, SidebarTabs } from '../Sidebar';
import { FeatureInfoPane } from '@/components/MapSidebarPanes';

// Types
import { GFIDisplayModeType, IGFICardOverlayData, IGFIOverlayFeatureIndex } from '@/@types/views/map';
import { ISidebarTabListItem } from '../Sidebar/SidebarTabListItem';
import PPInfoPane from '@/components/MapSidebarPanes/PP/PPInfoPane';
import { PPDocument } from '@/lib/prostorniPlanoviHelper';
import Permissions from '@/lib/permissions';

type GFISidebarControlProps = {
  gfiResults: IGFICardOverlayData[];
  onSidebarCollapse: (newState: boolean) => void;
  overlayFeatureIndex: IGFIOverlayFeatureIndex;
  setOverlayFeatureIndex: (layer: string, index: number) => void;
  onSidebarClose?: () => void;
  gfiDisplayMode: GFIDisplayModeType;
  overlayTabActive: number;
  setOverlayTabActive: (index: number) => void;
  onUndock?: () => void;
};

interface ISidebarPropsWIthRecord extends ISidebarTabListItem {
  initialRecord: IGFICardOverlayData;
  type: 'regular' | 'pp';
}

const GFISidebarControl: FunctionComponent<GFISidebarControlProps> = ({
  gfiResults,
  onSidebarCollapse,
  overlayFeatureIndex,
  setOverlayFeatureIndex,
  onSidebarClose,
  gfiDisplayMode,
  overlayTabActive,
  setOverlayTabActive,
  onUndock,
}) => {
  const [gfiTabItems, setGfiTabItems] = useState<ISidebarPropsWIthRecord[]>([]);

  const handleSidebarPaneChange = useCallback(
    (id: string) => {
      const layerIndex = gfiResults.findIndex((result) => result.layerData.layer === id);

      if (layerIndex !== -1) {
        setOverlayTabActive(layerIndex);
      } else {
        setOverlayTabActive(0);
      }
    },
    [gfiResults, setOverlayTabActive]
  );

  useEffect(() => {
    if (!gfiResults.length) {
      setGfiTabItems([]);
      return;
    }

    const tabs: ISidebarPropsWIthRecord[] = gfiResults.map((result) => {
      if (result.type === 'pp') {
        return {
          id: result.layerData.layer,
          icon: <DescriptionIcon />,
          title: (result.record?.planNameShort as string) || (result.record?.planName as string) || 'common.prostorni_plan',
          needPermission: [Permissions.ProstorniPlanoviView],
          initialRecord: result,
          type: 'pp',
        };
      }
      return {
        id: result.layerData.layer,
        icon: result.layerData.model?.icon ? <Icon>{result.layerData.model.icon}</Icon> : <InfoOutlineIcon />,
        title: result.layerData.title,
        needPermission: [],
        initialRecord: result,
        type: 'regular',
      };
    });

    setGfiTabItems(tabs);
  }, [gfiResults]);

  const panes = useMemo(
    () =>
      gfiTabItems.map((item) => {
        const result = item.initialRecord;
        if (!result) return null;
        if (item.type === 'pp') {
          return (
            <SidebarPane id={result.layerData.layer} key={item.id} noPadding limitHeight>
              <PPInfoPane
                doc={(result.record?.text as PPDocument) || []}
                clickPosition={result.position}
                planId={result.record?.planId as number}
                planName={result.record?.planName as string}
              />
            </SidebarPane>
          );
        }

        return (
          <SidebarPane id={item.id} key={item.id} noPadding>
            <FeatureInfoPane
              featureData={result}
              activeFeatureIndex={overlayFeatureIndex[item.id] ?? 0}
              setActiveFeatureIndex={setOverlayFeatureIndex}
              onUndock={onUndock}
            />
          </SidebarPane>
        );
      }),
    [gfiTabItems, overlayFeatureIndex, setOverlayFeatureIndex, onUndock]
  );

  const activeTabItem = gfiTabItems[overlayTabActive] ?? gfiTabItems[0];

  return (
    <SidebarControl
      onSidebarCollapse={onSidebarCollapse}
      id="sidebar-right"
      position="right"
      isHidden={gfiDisplayMode !== 'sidebar' || gfiResults.length === 0}
      // onTabChange={handleSidebarPaneChange}
      initialOpenId={activeTabItem?.id}
      initialOpenHeading={activeTabItem?.title}
    >
      <SidebarHeading onClose={onSidebarClose} />
      <SidebarTabs>
        <SidebarTabList>
          {gfiTabItems.map((item) => (
            <SidebarTabListItem
              key={item.id}
              id={item.id}
              icon={item.icon}
              title={item.title}
              disabled={false}
              needPermission={item.needPermission}
              disableUrlBehaviour
              onChange={handleSidebarPaneChange}
            />
          ))}
        </SidebarTabList>
      </SidebarTabs>
      <SidebarContent>{panes}</SidebarContent>
    </SidebarControl>
  );
};

export default GFISidebarControl;
